import type { MotionProps, Transition } from "framer-motion"
import { cn } from "./utils"

type Variants = NonNullable<MotionProps["variants"]>

/**
 * Единственный источник правды для движения интерфейса. Значения повторяют
 * CSS-токены `--dur-*` и `--ease-out-soft` из `app/styles/index.css`, чтобы
 * анимации на framer-motion и на классах Tailwind не расходились по ощущению.
 *
 * Анимируем только `opacity` и `transform`: внутри Telegram на слабом Android
 * анимация геометрии (height, top, width) заставляет браузер пересчитывать
 * раскладку на каждом кадре и заметно роняет частоту кадров.
 */

/** Кривая `--ease-out-soft`. Держим массивом: framer-motion не читает CSS-переменные. */
const EASE_OUT_SOFT = [0.22, 1, 0.36, 1] as const

export const TRANSITION = {
	/** Отклик на действие пользователя: нажатие, переключение. */
	fast: { duration: 0.14, ease: EASE_OUT_SOFT },
	/** Появление блоков и смена состояний. */
	base: { duration: 0.22, ease: EASE_OUT_SOFT },
	/** Смена скелетона на содержимое: ровное затухание, без рывка в начале. */
	crossfade: { duration: 0.36, ease: [0.4, 0, 0.2, 1] },
	/** Крупные перестроения экрана. */
	slow: { duration: 0.32, ease: EASE_OUT_SOFT },
	/** Для того, что должно ощущаться физически: переезд подложки, «поп» иконки. */
	spring: { type: "spring", stiffness: 420, damping: 32, mass: 0.7 },
	/**
	 * Перекрёстная смена блока: новое проявляется чуть дольше, чем гаснет старое, и они
	 * наплывают друг на друга. Ровная кривая: у `--ease-out-soft` почти вся смена приходится
	 * на первые кадры, и она читается подменой.
	 */
	swapIn: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
	swapOut: { duration: 0.24, ease: [0.4, 0, 0.2, 1] },
} satisfies Record<string, Transition>

/** Длительность смены высоты блока, мс. Совпадает с `--dur-slow`. */
export const HEIGHT_DURATION = 320
/**
 * Кривая смены высоты. Ровная: у `--ease-out-soft` блок за первый кадр проходит треть
 * пути, и соседи снизу как будто прыгают.
 */
export const HEIGHT_EASE = "cubic-bezier(0.4, 0, 0.2, 1)"
const HEIGHT_TRANSITION = {
	duration: HEIGHT_DURATION / 1000,
	ease: [0.4, 0, 0.2, 1],
} satisfies Transition

/**
 * Подтемнение блока, пока обновляются данные. Включается, только если ответа ждём дольше
 * 200 мс: на быстром ответе блок не мигает, а сразу остаётся в полной яркости.
 */
export const fetchingDim = (fetching: boolean) =>
	cn("transition-opacity", fetching ? "opacity-70 delay-200 duration-200" : "duration-150")

/** Появление снизу вверх: базовый вход секции или карточки. */
export const fadeUp: Variants = {
	hidden: { opacity: 0, y: 8 },
	visible: { opacity: 1, y: 0, transition: TRANSITION.base },
	exit: { opacity: 0, y: -4, transition: TRANSITION.fast },
}

/** Появление без смещения: там, где сдвиг мешает (оверлеи, подсказки). */
export const fadeIn: Variants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1, transition: TRANSITION.base },
	exit: { opacity: 0, transition: TRANSITION.fast },
}

/**
 * Строка списка, которая может добавиться или пропасть (удалили, отключили, создали).
 * Ставится на `motion.li` внутри `<AnimatePresence initial={false}>`: строка раскрывается
 * и сворачивается по высоте, поэтому список и всё, что ниже, сдвигаются плавно. Отступ
 * между строками задавайте внутренним `padding` строки, а не `space-y` или `gap` списка:
 * внешний отступ пропал бы рывком в конце.
 */
export const listItem = {
	initial: { opacity: 0, height: 0, overflow: "clip" },
	animate: {
		opacity: 1,
		height: "auto",
		transition: { height: HEIGHT_TRANSITION, opacity: { ...TRANSITION.base, delay: 0.06 } },
		transitionEnd: { overflow: "visible" },
	},
	exit: {
		opacity: 0,
		height: 0,
		overflow: "clip",
		pointerEvents: "none",
		transition: { height: HEIGHT_TRANSITION, opacity: TRANSITION.fast },
	},
} satisfies MotionProps

/** «Поп»: короткий всплеск масштаба для подтверждения действия. */
export const scalePop: Variants = {
	idle: { scale: 1 },
	pop: { scale: [1, 1.28, 1], transition: { duration: 0.32, times: [0, 0.4, 1] } },
}

/**
 * Контейнер со ступенчатым появлением детей. `stagger` держим маленьким:
 * на списке из шести карточек 0.04 s даёт ощущение «выкатывания», а 0.1 s
 * уже читается как задержка загрузки.
 */
export function staggerContainer(stagger = 0.04, delayChildren = 0): Variants {
	return {
		hidden: {},
		visible: { transition: { staggerChildren: stagger, delayChildren } },
		exit: {},
	}
}
